import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import formatURLS, { prepURLForFormatting } from '../../utils/formatURLS.v4';

/**
 * Enhance useLocation with custom data.
 */
const useHistory = (defaultUrl = '/') => {
  const router = useRouter();

  const goBackWithDefault = useCallback(() => {
    try {
      return router.back();
    } catch (error) {
      return router.push(defaultUrl);
    }
  }, [defaultUrl, router]);

  const listen = useCallback(
    (f) => {
      router.events.on('routeChangeStart', f);
      // If the component is unmounted, unsubscribe
      // from the event with the `off` method:
      return () => {
        router.events.off('routeChangeStart', f);
      };
    },
    [router],
  );

  /**
   * The function signatures for router.push / router.replace are
   * different between React Router and Next Router. These two
   * are just simple wrapper functions to appease the TS compiler.
   *
   * We can remove them after we've fully converted over to NextJS
   */

  const push = useCallback(
    (
      url: string | Record<string, any>,
      state?: Record<string, any>,
      options?: Record<string, any>,
    ) => {
      const { href, as } = formatURLS(
        prepURLForFormatting(url, state, router?.asPath),
      );

      router.push(href, as, { ...options, shallow: true });
    },
    [router],
  );

  const replace = useCallback(
    (
      url: string | Record<string, any>,
      state?: Record<string, any>,
      options?: Record<string, any>,
    ) => {
      const { href, as } = formatURLS(
        prepURLForFormatting(url, state, router?.asPath),
      );

      router.replace(href, as, { ...options, shallow: true });
    },
    [router],
  );

  return useMemo(
    () => ({ ...router, goBack: goBackWithDefault, listen, push, replace }),
    [goBackWithDefault, router, listen, push, replace],
  );
};

export default useHistory;
